import { imgAllowed } from "@/lib/url";
import type { Status } from "./data";
import {
  StatusDndIcon,
  StatusIdleIcon,
  StatusOfflineIcon,
  StatusOnlineIcon,
  StatusSleepyIcon,
  StatusWorkIcon,
} from "./icons";

/* status is a corner glyph — just the tabler icon in the status color,
   no disc or punch-out behind it */
const DOT: Record<Status, { fg: string; Icon: typeof StatusOnlineIcon }> =
  {
    online: { fg: "text-online", Icon: StatusOnlineIcon },
    idle: { fg: "text-idle", Icon: StatusIdleIcon },
    dnd: { fg: "text-dnd", Icon: StatusDndIcon },
    sleeping: { fg: "text-sleepy", Icon: StatusSleepyIcon },
    work: { fg: "text-work", Icon: StatusWorkIcon },
    offline: { fg: "text-fg-faint", Icon: StatusOfflineIcon },
  };

/* The one pfp component — placeholder block or initials, square-ish
   rounding, and a status dot punched out at the bottom-right. */
export function Pfp({
  status,
  size = 36,
  rounded = "rounded-lg",
  initials,
  color,
  src,
}: {
  status?: Status;
  size?: number;
  rounded?: string;
  /* deprecated — was the badge cutout surface; kept so old callers
     still typecheck */
  surface?: string;
  initials?: string;
  color?: string;
  /* image avatar — wins over initials/color placeholder */
  src?: string;
}) {
  /* image slots never fall back to text — an unapproved origin just
     renders the empty block */
  const img = imgAllowed(src);
  const dot = status ? DOT[status] : null;
  const dotSize = Math.max(11, Math.round(size * 0.42));
  return (
    <span
      className="relative inline-flex shrink-0"
      style={{ width: size, height: size }}
    >
      {src ? (
        img ? (
          <span
            aria-hidden
            className={`block h-full w-full bg-cover bg-center ${rounded}`}
            style={{ backgroundImage: `url(${img})` }}
          />
        ) : (
          <span
            aria-hidden
            className={`block h-full w-full bg-lift ${rounded}`}
          />
        )
      ) : (
        <span
          className={`grid h-full w-full place-items-center font-semibold text-white ${rounded} ${
            color ? "" : "bg-lift"
          }`}
          style={{
            backgroundColor: color,
            fontSize: Math.max(10, Math.round(size * 0.33)),
          }}
        >
          {initials}
        </span>
      )}
      {dot ? (
        <dot.Icon
          aria-hidden
          halo
          className={`absolute -bottom-1 -right-1 ${dot.fg}`}
          style={{ width: dotSize, height: dotSize }}
        />
      ) : null}
    </span>
  );
}
